import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { db } from '@/lib/db';
import { exportSong, downloadExport } from '@/lib/exportImport';
import { formatDate, formatDuration } from '@/lib/utils';
import { FileText, FileAudio, Package, Loader2, Download, Check, Share2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ExportDialog({ open, onOpenChange, song }) {
    const [exporting, setExporting] = useState(null);
    const [exported, setExported] = useState([]);

    if (!song) return null;

    const textFragments = song.fragments.filter(f => f.type === 'text');
    const audioFragments = song.fragments.filter(f => f.type === 'audio');
    const metadataFragment = song.fragments.find(f => f.type === 'metadata');
    const instrumentFragments = song.fragments.filter(f => f.type === 'instrument');
    const tuningFragments = song.fragments.filter(f => f.type === 'tuning');
    const linkFragments = song.fragments.filter(f => f.type === 'link');
    const inspirationFragments = song.fragments.filter(f => f.type === 'inspiration');
    const droneFragments = song.fragments.filter(f => f.type === 'drone');

    // Export lyrics as text file
    const exportLyrics = async () => {
        setExporting('lyrics');
        try {
            let content = `${song.title || 'Untitled Song'}\n`;
            content += `${'='.repeat(40)}\n\n`;
            
            if (metadataFragment) {
                if (metadataFragment.bpm) content += `BPM: ${metadataFragment.bpm}\n`;
                if (metadataFragment.key) content += `Key: ${metadataFragment.key}\n`;
                if (metadataFragment.feel) content += `Feel: ${metadataFragment.feel}\n`;
                content += '\n';
            }

            textFragments.forEach((fragment, index) => {
                content += `[${fragment.label || 'Note'}]\n`;
                content += `${fragment.content}\n\n`;
            });

            content += `\n---\nExported from Songwriter's Toolbox\n${formatDate(new Date())}`;

            const blob = new Blob([content], { type: 'text/plain' });
            downloadFile(blob, `${sanitizeFilename(song.title || 'untitled')}-lyrics.txt`);
            setExported(prev => [...prev, 'lyrics']);
            toast.success('Lyrics exported');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export lyrics');
        } finally {
            setExporting(null);
        }
    };

    // Export all audio files
    const exportAudio = async () => {
        setExporting('audio');
        try {
            for (const fragment of audioFragments) {
                if (fragment.blobId) {
                    const blob = await db.getAudioBlob(fragment.blobId);
                    if (blob) {
                        const extension = fragment.mimeType?.includes('webm') ? 'webm' : 'mp4';
                        const filename = `${sanitizeFilename(song.title || 'untitled')}-${sanitizeFilename(fragment.label || 'recording')}-${fragment.id.slice(-6)}.${extension}`;
                        downloadFile(blob, filename);
                    }
                }
            }
            setExported(prev => [...prev, 'audio']);
            toast.success(`${audioFragments.length} audio file(s) exported`);
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export audio');
        } finally {
            setExporting(null);
        }
    };

    // Export full song package as text (simplified PDF alternative)
    const exportPackage = async () => {
        setExporting('package');
        try {
            let content = `SONG PACKAGE\n`;
            content += `${'='.repeat(50)}\n\n`;
            content += `Title: ${song.title || 'Untitled Song'}\n`;
            content += `Created: ${formatDate(song.createdAt)}\n`;
            content += `Last Modified: ${formatDate(song.updatedAt)}\n\n`;

            // Metadata
            if (metadataFragment) {
                content += `SONG INFO\n${'-'.repeat(30)}\n`;
                if (metadataFragment.bpm) content += `BPM: ${metadataFragment.bpm}\n`;
                if (metadataFragment.key) content += `Key: ${metadataFragment.key}\n`;
                if (metadataFragment.feel) content += `Feel: ${metadataFragment.feel}\n`;
                content += '\n';
            }

            // Instrument notes
            if (instrumentFragments.length > 0) {
                content += `INSTRUMENT NOTES\n${'-'.repeat(30)}\n`;
                instrumentFragments.forEach(frag => {
                    if (frag.instrument) content += `Instrument: ${frag.instrument}\n`;
                    if (frag.tuning) content += `Tuning: ${frag.tuning}\n`;
                    if (frag.capo !== undefined) content += `Capo: ${frag.capo === 0 ? 'None' : `Fret ${frag.capo}`}\n`;
                    if (frag.chords) content += `Chords: ${frag.chords}\n`;
                    if (frag.notes) content += `Notes: ${frag.notes}\n`;
                    content += '\n';
                });
            }

            // Tuning records
            if (tuningFragments.length > 0) {
                content += `TUNING RECORDS\n${'-'.repeat(30)}\n`;
                tuningFragments.forEach(frag => {
                    content += `• ${frag.targetString || 'Auto'}: `;
                    if (frag.detectedNote) content += `${frag.detectedNote} `;
                    if (frag.detectedFrequency) content += `(${frag.detectedFrequency} Hz) `;
                    if (frag.cents !== null && frag.cents !== undefined) {
                        content += frag.inTune ? '✓ In tune' : `${frag.cents > 0 ? '+' : ''}${frag.cents}¢`;
                    }
                    content += `\n  Recorded: ${formatDate(frag.createdAt)}\n`;
                });
                content += '\n';
            }

            // External links
            if (linkFragments.length > 0) {
                content += `EXTERNAL LINKS\n${'-'.repeat(30)}\n`;
                linkFragments.forEach(frag => {
                    content += `• ${frag.label || 'Link'}: ${frag.url}\n`;
                    if (frag.description) content += `  ${frag.description}\n`;
                });
                content += '\n';
            }

            // Inspiration/References
            if (inspirationFragments.length > 0) {
                content += `REFERENCES\n${'-'.repeat(30)}\n`;
                inspirationFragments.forEach(frag => {
                    content += `• ${frag.label || 'Reference'}: ${frag.url}\n`;
                    if (frag.embedType) content += `  Type: ${frag.embedType}\n`;
                    if (frag.note) content += `  Note: ${frag.note}\n`;
                });
                content += '\n';
            }

            // Drone references
            if (droneFragments.length > 0) {
                content += `DRONE REFERENCES\n${'-'.repeat(30)}\n`;
                droneFragments.forEach(frag => {
                    content += `• ${frag.note} (${frag.frequency} Hz)\n`;
                    content += `  Created: ${formatDate(frag.createdAt)}\n`;
                });
                content += '\n';
            }

            // Lyrics/Text
            if (textFragments.length > 0) {
                content += `LYRICS & NOTES\n${'-'.repeat(30)}\n\n`;
                textFragments.forEach(frag => {
                    content += `[${frag.label || 'Note'}]\n`;
                    content += `${frag.content}\n\n`;
                });
            }

            // Audio index
            if (audioFragments.length > 0) {
                content += `AUDIO RECORDINGS\n${'-'.repeat(30)}\n`;
                audioFragments.forEach((frag, index) => {
                    content += `${index + 1}. ${frag.label || 'Recording'} (${formatDuration(frag.duration)})\n`;
                    content += `   Created: ${formatDate(frag.createdAt)}\n`;
                });
                content += '\n';
            }

            content += `\n${'='.repeat(50)}\n`;
            content += `Exported from Songwriter's Toolbox\n`;
            content += `${formatDate(new Date())}\n`;

            const blob = new Blob([content], { type: 'text/plain' });
            downloadFile(blob, `${sanitizeFilename(song.title || 'untitled')}-package.txt`);
            setExported(prev => [...prev, 'package']);
            toast.success('Song package exported');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export package');
        } finally {
            setExporting(null);
        }
    };

    // Export portable format (.stb.json) for importing into another instance
    const exportPortable = async () => {
        setExporting('portable');
        try {
            const exportData = await exportSong(song);
            downloadExport(exportData, sanitizeFilename(song.title || 'untitled'));
            setExported(prev => [...prev, 'portable']);
            toast.success('Portable export ready');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export');
        } finally {
            setExporting(null);
        }
    };

    const downloadFile = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const sanitizeFilename = (name) => {
        return name.replace(/[^a-z0-9]/gi, '-').toLowerCase().replace(/-+/g, '-').slice(0, 50);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">Export Song</DialogTitle>
                    <DialogDescription>
                        Download your song data. Everything stays on your device — this is your work.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-4">
                    {/* Lyrics Export */}
                    <Button
                        variant="outline"
                        onClick={exportLyrics}
                        disabled={textFragments.length === 0 || exporting === 'lyrics'}
                        className="w-full justify-start h-auto py-4 px-4"
                    >
                        <div className="w-10 h-10 rounded-lg bg-fragment-text/15 flex items-center justify-center mr-4">
                            {exporting === 'lyrics' ? (
                                <Loader2 className="w-5 h-5 text-fragment-text animate-spin" />
                            ) : exported.includes('lyrics') ? (
                                <Check className="w-5 h-5 text-success" />
                            ) : (
                                <FileText className="w-5 h-5 text-fragment-text" />
                            )}
                        </div>
                        <div className="text-left flex-1">
                            <span className="font-medium block">Export Lyrics</span>
                            <span className="text-sm text-muted-foreground">
                                {textFragments.length} text fragment{textFragments.length !== 1 ? 's' : ''} as .txt
                            </span>
                        </div>
                        <Download className="w-4 h-4 text-muted-foreground" />
                    </Button>

                    {/* Audio Export */}
                    <Button
                        variant="outline"
                        onClick={exportAudio}
                        disabled={audioFragments.length === 0 || exporting === 'audio'}
                        className="w-full justify-start h-auto py-4 px-4"
                    >
                        <div className="w-10 h-10 rounded-lg bg-fragment-audio/15 flex items-center justify-center mr-4">
                            {exporting === 'audio' ? (
                                <Loader2 className="w-5 h-5 text-fragment-audio animate-spin" />
                            ) : exported.includes('audio') ? (
                                <Check className="w-5 h-5 text-success" />
                            ) : (
                                <FileAudio className="w-5 h-5 text-fragment-audio" />
                            )}
                        </div>
                        <div className="text-left flex-1">
                            <span className="font-medium block">Export Audio</span>
                            <span className="text-sm text-muted-foreground">
                                {audioFragments.length} recording{audioFragments.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                        <Download className="w-4 h-4 text-muted-foreground" />
                    </Button>

                    {/* Full Package Export */}
                    <Button
                        variant="outline"
                        onClick={exportPackage}
                        disabled={song.fragments.length === 0 || exporting === 'package'}
                        className="w-full justify-start h-auto py-4 px-4"
                    >
                        <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center mr-4">
                            {exporting === 'package' ? (
                                <Loader2 className="w-5 h-5 text-primary animate-spin" />
                            ) : exported.includes('package') ? (
                                <Check className="w-5 h-5 text-success" />
                            ) : (
                                <Package className="w-5 h-5 text-primary" />
                            )}
                        </div>
                        <div className="text-left flex-1">
                            <span className="font-medium block">Full Song Package</span>
                            <span className="text-sm text-muted-foreground">
                                All info, lyrics & audio index
                            </span>
                        </div>
                        <Download className="w-4 h-4 text-muted-foreground" />
                    </Button>

                    {/* Portable Export for sharing between instances */}
                    <Button
                        variant="outline"
                        onClick={exportPortable}
                        disabled={song.fragments.length === 0 || exporting === 'portable'}
                        className="w-full justify-start h-auto py-4 px-4 border-dashed"
                    >
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mr-4">
                            {exporting === 'portable' ? (
                                <Loader2 className="w-5 h-5 text-foreground animate-spin" />
                            ) : exported.includes('portable') ? (
                                <Check className="w-5 h-5 text-success" />
                            ) : (
                                <Share2 className="w-5 h-5 text-foreground" />
                            )}
                        </div>
                        <div className="text-left flex-1">
                            <span className="font-medium block">Portable Export</span>
                            <span className="text-sm text-muted-foreground">
                                .stb.json — import into another Toolbox
                            </span>
                        </div>
                        <Download className="w-4 h-4 text-muted-foreground" />
                    </Button>
                </div>

                <p className="text-xs text-center text-muted-foreground pb-2">
                    All data stored locally on your device.
                </p>
            </DialogContent>
        </Dialog>
    );
}